import type { CityConfig } from "../city-template/types";

export const rehobothBeachConfig: CityConfig = {
  city: "Rehoboth Beach",
  state: "Delaware",
  stateAbbr: "DE",
  slug: "rehoboth-beach",
  route: "/rehoboth-beach",
  title: "Emergency Passport Rehoboth Beach DE | Expedited Passport Sussex County Coastal Delaware | Fast Passport Center",
  metaDescription: "Emergency passport and expedited passport services in Rehoboth Beach, DE — as fast as 24 hours. Fast passport renewal for Sussex County coastal Delaware travelers, beach vacationers, and resort residents. A+ BBB rated.",
  h1: "Emergency & Expedited Passport Services in Rehoboth Beach, DE",
  heroSubtitle: "Planning a beach vacation or international cruise from the Delaware coast and need a passport fast? Fast Passport Center helps Rehoboth Beach, Lewes, Dewey Beach, and Bethany Beach travelers get their expedited passports as quickly as 24 hours. A+ BBB rated.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Rehoboth%20Beach%20Delaware%20coastline%20at%20golden%20hour%2C%20sandy%20beach%20with%20gentle%20Atlantic%20Ocean%20waves%2C%20colorful%20beach%20boardwalk%20buildings%20in%20the%20background%2C%20families%20walking%20along%20the%20shoreline%2C%20warm%20sunset%20light%20reflecting%20on%20wet%20sand%2C%20peaceful%20coastal%20vacation%20atmosphere%2C%20no%20visible%20text%2C%20photorealistic%20travel%20photography%20style&width=1800&height=700&seq=rehoboth-beach-hero-001&orientation=landscape",
  heroImageAlt: "Rehoboth Beach Delaware coastline at sunset with Atlantic Ocean waves and sandy shore",
  geoPosition: "38.7209;-75.0760",
  geoPlacename: "Rehoboth Beach, Delaware",
  lastModified: "2026-06-01",
  regionalAgency: {
    name: "Philadelphia Passport Agency",
    address: "600 Arch St Suite 4200, Philadelphia, PA 19106",
    distance: "Approximately 2.5 hours from Rehoboth Beach, DE via US-13 N and I-95 N",
  },
  nearbyCitiesZips: [
    { name: "Rehoboth Beach", zips: "19971, 19958" },
    { name: "Lewes", zips: "19958" },
    { name: "Dewey Beach", zips: "19971" },
    { name: "Bethany Beach", zips: "19930" },
    { name: "Millsboro", zips: "19966" },
    { name: "Georgetown", zips: "19947" },
    { name: "Seaford", zips: "19973" },
    { name: "Ocean View", zips: "19970" },
    { name: "Long Neck", zips: "19966" },
    { name: "Milford", zips: "19963" },
    { name: "Bridgeville", zips: "19933" },
    { name: "Selbyville", zips: "19975" },
    { name: "Fenwick Island", zips: "19944" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier for coastal Delaware travelers with cruise and beach departure deadlines" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-sun-line", title: "Coastal Delaware Specialist", desc: "Serving Rehoboth, Lewes, Dewey Beach, Bethany Beach, and the Delaware resort area" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels — no 2.5-hour drive to Philadelphia" },
  ],
  internalLinks: [
    { name: "Expedited passport in Wilmington, DE", route: "/wilmington-de" },
    { name: "Emergency passport help in Dover, DE", route: "/dover-de" },
    { name: "Urgent passport services in Newark, DE", route: "/newark-de" },
    { name: "Delaware passport services overview", route: "/delaware" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Rehoboth Beach, Delaware remotely via secure registered courier. We do not have a walk-in office in Rehoboth Beach — our registered couriers hand-deliver your documents to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for Sussex County coastal Delaware travelers without the long 2.5-hour drive to Philadelphia.",
  fpcOfficeAddress: "1700 Market St, Suite 1005, Philadelphia, PA 19103",
  fpcOfficeDistance: "Serving Rehoboth Beach, Lewes, Dewey Beach, Bethany Beach, and all of Sussex County",
  serviceAreaIntro: "Fast Passport Center provides emergency and expedited passport services throughout the Rehoboth Beach, Delaware area and all of Sussex County. From the boardwalk and beachfront communities to the inland resort towns of Millsboro and Georgetown, from Lewes to Bethany Beach — we serve all of coastal Delaware's vacation and residential communities.",
  nearbyCitiesIntro: "We serve Rehoboth Beach and nearby coastal communities including Lewes, Dewey Beach, Bethany Beach, Ocean View, Long Neck, Millsboro, Georgetown, Seaford, Bridgeville, Selbyville, and Fenwick Island. We also serve inland Sussex County residents from Milford to Laurel. If you are anywhere on the Delaware coast, we have you covered.",
  howItWorksIntro: "Most Rehoboth Beach travelers complete everything online and ship documents overnight. No need to drive 2.5 hours to Philadelphia or take a day off from your beach vacation planning.",
  mistakesIntro: "Rehoboth Beach, Delaware has a unique mix of seasonal vacationers, year-round coastal residents, and retirees. Here are the most common passport mistakes we see from Sussex County travelers.",
  testimonialsIntro: "Rehoboth Beach, Delaware travelers — from vacationing families to retirees — trust us when their cruise or international departure is days away.",
  faqIntro: "Have questions about expedited passports in Rehoboth Beach, DE? Here are the most common ones we hear from coastal Delaware travelers.",
  statePageRoute: "/states/delaware",
  statePageName: "Delaware",
  dmvWebsite: "https://www.dmv.de.gov",
  dmvLabel: "Delaware DMV — Official Website",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: ["Appointment required — no walk-ins allowed", "Life-or-death emergencies only", "Must drive 2.5 hours to Philadelphia", "Full day commitment — impractical for coastal travelers"],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Predictable turnaround", "Perfect for cruise and vacation deadlines", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Faster than self-service", "Good for planned travel", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: ["No courier access — standard mail only", "Workload & wait times shift with shutdowns and budget fights", "In-person clerk appointment required", "No document review — errors cause rejection", "No dedicated agent — you are on your own", "Updates only by physical mail letter", "No phone or email contact for your case", "Can't upgrade speed after submission", "Not suitable if travel is within 10 weeks"],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For Rehoboth Beach travelers with immediate departure needs — as fast as 24 hours. Perfect for last-minute cruise bookings and beach vacation plans.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Cruise and vacation travel support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for Rehoboth Beach adults and retirees with a passport issued within the last 15 years.",
      bullets: ["No clerk visit required for most renewals", "Mail-in or courier options", "Returned to your coastal Delaware address"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport (First-Time)",
      description: "First-time passport application for Rehoboth Beach residents and seasonal visitors — including first international beach vacations.",
      bullets: ["Acceptance facility scheduling", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Emergency passport replacement for Rehoboth Beach and Sussex County residents.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement in 24–48 hours"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 — for coastal Delaware families with kids traveling internationally.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change Passport",
      description: "Passport name correction after marriage, divorce, or legal name changes for Rehoboth Beach Delaware residents.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport Rehoboth Beach DE", description: "Urgent passport processing for Rehoboth Beach Delaware travelers with immediate departure needs including cruise and beach vacations." },
    { name: "Expedited Passport Renewal Rehoboth Beach DE", description: "Fast passport renewal for Rehoboth Beach Delaware residents and retirees with a previously-issued passport." },
    { name: "First-Time Passport Rehoboth Beach DE", description: "New passport application services for Rehoboth Beach Delaware residents and coastal visitors." },
    { name: "Child Passport Rehoboth Beach DE", description: "Minor passport services for Rehoboth Beach Delaware families with children under 16." },
  ],
  testimonials: [
    {
      name: "Patricia R.",
      location: "Rehoboth Beach, DE",
      rating: 5,
      text: "Booked a Caribbean cruise for our anniversary and realized my passport was expired with only 10 days to go. Fast Passport Center had my renewed passport in 4 days. Saved our dream vacation.",
      service: "Passport Renewal",
    },
    {
      name: "Michael T.",
      location: "Lewes, DE",
      rating: 5,
      text: "Retired in Lewes and decided to take a European river cruise. First-time passport at 68 years old. They made the process so simple. Recommending them to everyone at my beach community.",
      service: "First-Time Passport",
    },
    {
      name: "Sarah K.",
      location: "Dewey Beach, DE",
      rating: 5,
      text: "Our entire family needed passports for a Mexico vacation. Six passports, one week. Fast Passport Center coordinated everything. I cannot believe how smooth it was from the Delaware coast.",
      service: "Family Passport Bundle",
    },
    {
      name: "James W.",
      location: "Bethany Beach, DE",
      rating: 5,
      text: "Lost my passport three days before a business trip to London. Absolute panic. They had a replacement in my hands in 36 hours. Truly lifesaving for a Bethany Beach resident.",
      service: "Lost Passport",
    },
  ],
  facilities: [
    {
      id: "rehoboth-de-1",
      name: "Rehoboth Beach Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "43 Henlopen Ave",
      city: "Rehoboth Beach",
      state: "DE",
      zip: "19971",
      phone: "(302) 227-6611",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Near the Rehoboth Beach boardwalk",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Primary acceptance facility for Rehoboth Beach and Dewey Beach. Appointments required — book 1–2 weeks in advance.",
      icon: "ri-mail-line",
    },
    {
      id: "rehoboth-de-2",
      name: "Lewes Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "126 E Savannah Rd",
      city: "Lewes",
      state: "DE",
      zip: "19958",
      phone: "(302) 645-6414",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Approximately 5 miles north of Rehoboth Beach",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Lewes and northern coastal Delaware residents. Call to confirm current passport acceptance hours.",
      icon: "ri-mail-line",
    },
    {
      id: "rehoboth-de-3",
      name: "Bethany Beach Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "103 Garfield Pkwy",
      city: "Bethany Beach",
      state: "DE",
      zip: "19930",
      phone: "(302) 539-4496",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Approximately 12 miles south of Rehoboth Beach",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serving southern coastal Delaware from Bethany Beach to Fenwick Island. Limited hours — call ahead.",
      icon: "ri-mail-line",
    },
    {
      id: "rehoboth-de-4",
      name: "Millsboro Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "100 E Main St",
      city: "Millsboro",
      state: "DE",
      zip: "19966",
      phone: "(302) 934-7161",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Approximately 15 miles inland from Rehoboth Beach",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Inland option for Sussex County residents in Long Neck and surrounding areas.",
      icon: "ri-mail-line",
    },
    {
      id: "rehoboth-de-5",
      name: "Sussex County Clerk of the Peace",
      type: "County Clerk Passport Acceptance",
      address: "2 The Circle",
      city: "Georgetown",
      state: "DE",
      zip: "19947",
      phone: "(302) 855-7785",
      hours: "Mon–Fri: 8:30 AM – 4:30 PM",
      distance: "Approximately 25 miles from Rehoboth Beach in Georgetown",
      website: "https://www.sussexcountyde.gov/",
      websiteLabel: "Sussex County",
      notes: "County-level facility with full passport acceptance. Extended hours compared to seasonal post offices. Good for Sussex County residents.",
      icon: "ri-government-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited Philadelphia slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — 2.5 hour drive to Philadelphia", fpc: "No — ship from Rehoboth Beach" },
    { feature: "Cruise Deadline Support", agency: "No flexibility for cruise schedules", fpc: "Yes — we understand cruise deadlines" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
  ],
  mistakes: [
    {
      icon: "ri-time-line",
      title: "Booking a Cruise Before Checking Passport Validity",
      description: "Coastal Delaware travelers often book cruises and international beach vacations before checking if their passport is valid. Many countries require 6 months validity beyond your return date. Check before booking.",
    },
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Beach resort area photo services sometimes produce photos that meet general ID requirements but not passport standards. Passport photos must be exactly 2x2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 rejection reasons at Rehoboth Beach and Sussex County acceptance facilities.",
    },
    {
      icon: "ri-sun-line",
      title: "Assuming Seasonal Post Offices Are Open",
      description: "Some Sussex County post offices have reduced hours during the off-season. Always call ahead to confirm passport acceptance hours before making the trip.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Government fees change periodically. Always verify current fees before submitting — incorrect payment means starting over.",
    },
    {
      icon: "ri-time-line",
      title: "Underestimating Routine Processing Time",
      description: "Rehoboth Beach residents often assume 4–6 weeks is enough. Routine processing takes 6–13 weeks. If your cruise or vacation is within 10 weeks, use expedited service.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes — from your beach house or condo in Rehoboth Beach." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location near Rehoboth Beach or Lewes." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy — no rejections for small errors." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your coastal Delaware address — trackable every step of the way." },
  ],
  faqs: [
    { q: "How fast can I get an emergency passport in Rehoboth Beach, DE?", a: "Fast Passport Center offers emergency passport processing in Rehoboth Beach, Delaware in as little as 24–48 hours. Our registered couriers hand-deliver your documents to the Philadelphia Passport Agency (2.5 hours from Rehoboth Beach) — without you needing to make the drive." },
    { q: "Does Rehoboth Beach, Delaware have its own passport agency?", a: "No — neither Rehoboth Beach nor the state of Delaware has a regional passport agency. The closest facility is the Philadelphia Passport Agency, approximately 2.5 hours from Rehoboth Beach. Fast Passport Center's courier service means coastal Delaware travelers never need to make that drive." },
    { q: "Can I get an expedited passport if I live in Lewes or Bethany Beach?", a: "Absolutely. Fast Passport Center serves all of Sussex County including Lewes, Dewey Beach, Bethany Beach, Ocean View, Long Neck, and Fenwick Island. Our service works the same way — complete your application online, ship your documents, and we handle the rest." },
    { q: "How much does an expedited passport cost in Rehoboth Beach, DE?", a: "Government fees are fixed: $130 for a first-time passport book or $130 + $60 expedite for a renewed adult passport. Fast Passport Center's service fees vary by processing speed. Contact us for a quote based on your cruise or vacation departure date." },
    { q: "What airports do Rehoboth Beach, Delaware travelers use?", a: "Most Sussex County travelers fly internationally through Philadelphia International Airport (PHL), approximately 2.5 hours north. Some use Baltimore-Washington International (BWI) at about 2 hours west. For domestic flights, many use the smaller Salisbury-Ocean City Wicomico Regional Airport (SBY) in Maryland. Always verify your passport is valid for at least 6 months past your return date." },
    { q: "Can I get a passport for my whole family in Rehoboth Beach?", a: "Yes. Fast Passport Center handles multi-passport cases for families in Rehoboth Beach, Lewes, and Bethany Beach. Whether you need child passports, renewals, or first-time applications for the whole family, we coordinate the process to ensure everyone is ready for your cruise or vacation." },
    { q: "Where is the closest passport acceptance facility to Rehoboth Beach?", a: "The Rehoboth Beach Post Office at 43 Henlopen Ave is the primary local acceptance facility. The Lewes Post Office at 126 E Savannah Rd is also nearby for northern coastal residents. For county-level services, the Sussex County Clerk of the Peace in Georgetown at 2 The Circle offers extended hours. All require appointments." },
    { q: "How do I know if I need expedited or routine passport processing for my cruise?", a: "If your cruise or vacation is within 10 weeks, use expedited service. If your travel is within 2–3 weeks, use emergency expedited service. Routine processing takes 6–13 weeks and is only suitable for travel more than 3 months away. Cruise passengers should note that some cruise lines require passports 90 days before sailing." },
  ],
  photoLocations: [
    { name: "Walgreens — Rehoboth Beach", address: "19066 Coastal Hwy, Rehoboth Beach, DE 19971", hours: "Mon–Sun: 7AM–10PM" },
    { name: "CVS Pharmacy — Rehoboth Beach", address: "19270 Coastal Hwy, Rehoboth Beach, DE 19971", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walmart — Rehoboth Beach", address: "18922 Rehoboth Mall Blvd, Rehoboth Beach, DE 19971", hours: "Mon–Sun: 6AM–11PM" },
    { name: "Walgreens — Lewes", address: "17644 Coastal Hwy, Lewes, DE 19958", hours: "Mon–Sun: 7AM–10PM" },
  ],
  dmvLocations: [
    { name: "DMV — Georgetown", address: "1 The Circle, Georgetown, DE 19947", hours: "Mon–Fri: 8AM–4:30PM", note: "Serves all of Sussex County. REAL ID processing available.", featured: true },
    { name: "DMV — Dover", address: "3030 S Dupont Hwy, Dover, DE 19901", hours: "Mon–Fri: 8AM–4:30PM", note: "Alternative for northern Sussex County residents. REAL ID processing available.", featured: false },
  ],
  airports: [
    {
      code: "PHL",
      name: "Philadelphia International Airport",
      distance: "Approximately 130 miles from Rehoboth Beach, DE",
      address: "8000 Essington Ave, Philadelphia, PA 19153",
      note: "Primary international departure airport for Rehoboth Beach travelers. 2.5 hour drive north. Major international hub with direct flights to Europe, the Caribbean, and beyond.",
      flightUrl: "https://www.phl.org/",
      flightLabel: "PHL Flight Info",
      featured: true,
    },
    {
      code: "BWI",
      name: "Baltimore-Washington International",
      distance: "Approximately 120 miles from Rehoboth Beach, DE",
      address: "7062 Elm Rd, Baltimore, MD 21240",
      note: "Alternative international option for Sussex County travelers. 2 hour drive west. Good for budget airlines and international connections.",
      flightUrl: "https://www.bwiairport.com/",
      flightLabel: "BWI Airport",
      featured: false,
    },
    {
      code: "SBY",
      name: "Salisbury Regional Airport",
      distance: "Approximately 40 miles from Rehoboth Beach, DE",
      address: "5485 Airport Terminal Rd, Salisbury, MD 21804",
      note: "Closest regional airport for Sussex County. Limited domestic service only. No major international connections.",
      flightUrl: "https://www.flysbyairport.com/",
      flightLabel: "SBY Airport",
      featured: false,
    },
  ],
};